import { ChainId } from '@src/configs/chainInfo'
import { IToken } from '@src/types/token'
import { isFeatureEnabled } from '@src/utils/environment'

// Helper function to compare gas tokens priority
export const compareGasTokens = (a: IToken, b: IToken): number => {
    // Native tokens (like ETH) should come first
    if (a.native && !b.native) return -1
    if (!a.native && b.native) return 1

    // Then wrapped versions (like WETH)
    if (a.wrapped && !b.wrapped) return -1
    if (!a.wrapped && b.wrapped) return 1

    return 0
}

// Helper function to sort tokens by priority
export const sortTokensByPriority = (tokens: IToken[], query?: string): IToken[] => {
    return tokens.sort((a, b) => {
        // First prioritize gas tokens
        const gasTokenComparison = compareGasTokens(a, b)
        if (gasTokenComparison !== 0) return gasTokenComparison

        // Then sort by exact symbol match if query exists
        if (query) {
            const lowercaseQuery = query.toLowerCase()
            const aExactMatch = a.symbol.toLowerCase() === lowercaseQuery
            const bExactMatch = b.symbol.toLowerCase() === lowercaseQuery
            if (aExactMatch && !bExactMatch) return -1
            if (!aExactMatch && bExactMatch) return 1
        }

        // Then sort by user balance
        const aBalance = a.userBalanceInUSD ?? 0
        const bBalance = b.userBalanceInUSD ?? 0
        if (aBalance && bBalance) {
            return bBalance - aBalance
        } else if (aBalance) {
            return -1
        } else if (bBalance) {
            return 1
        }

        // Finally sort by market cap
        const aCap = a.market_cap ?? -Infinity
        const bCap = b.market_cap ?? -Infinity
        return bCap - aCap
    })
}

// Helper function to filter tokens by selected token
export const filterBySelectedToken = (tokens: IToken[], selectedToken?: IToken, side?: 'buy' | 'sell'): IToken[] => {
    // Always show all tokens in search results
    return tokens
}

// Map of common blockchain search terms to chain IDs
export const CHAIN_SEARCH_TERMS: Record<string, number[]> = {
    // Main networks
    solana: [ChainId.Solana],
    sol: [ChainId.Solana],
    ethereum: [ChainId.Ethereum],
    eth: [ChainId.Ethereum],
    bitcoin: [ChainId.Bitcoin],
    btc: [ChainId.Bitcoin],
    tron: [ChainId.Tron],
    trx: [ChainId.Tron],
    polygon: [ChainId.Polygon],
    matic: [ChainId.Polygon],
    pol: [ChainId.Polygon],
    binance: [ChainId.Bsc],
    bnb: [ChainId.Bsc],
    bsc: [ChainId.Bsc],
    avalanche: [ChainId.Avalanche],
    avax: [ChainId.Avalanche],
    arbitrum: [ChainId.Arbitrum],
    arb: [ChainId.Arbitrum],
    optimism: [ChainId.Optimism],
    op: [ChainId.Optimism],
    base: [ChainId.Base],
    blast: [ChainId.Blast],
    scroll: [ChainId.Scroll],
    mantle: [ChainId.Mantle],
    mode: [ChainId.Mode],
    celo: [ChainId.Celo],

    // Categories
    evm: [
        ChainId.Ethereum,
        ChainId.Polygon,
        ChainId.Bsc,
        ChainId.Avalanche,
        ChainId.Arbitrum,
        ChainId.Optimism,
        ChainId.Base,
        ChainId.Blast,
        ChainId.Scroll,
        ChainId.Mantle,
        ChainId.Mode,
        ChainId.Celo,
    ],
    l2: [ChainId.Arbitrum, ChainId.Optimism, ChainId.Base, ChainId.Scroll],
    layer2: [ChainId.Arbitrum, ChainId.Optimism, ChainId.Base, ChainId.Scroll],
}

// Helper function to filter tokens by search query
export const filterTokensByQuery = (
    tokens: IToken[],
    query: string,
    selectedToken?: IToken,
    side?: 'buy' | 'sell'
): IToken[] => {
    const lowercaseQuery = query.toLowerCase().trim()

    // Check if query matches a blockchain name or symbol
    const matchingChainIds = CHAIN_SEARCH_TERMS[lowercaseQuery]

    if (matchingChainIds) {
        // If query exactly matches a blockchain name/symbol, filter by chain ID
        return tokens.filter(token => {
            // Check if token's chain ID is in the matching chain IDs
            const matchesChain = matchingChainIds.includes(token.chainId)

            if (!matchesChain) return false

            // Additional filtering for selected token
            return filterBySelectedToken([token], selectedToken, side).length > 0
        })
    }

    // For partial matches, check if query is part of a blockchain name
    let partialChainMatches: number[] = []
    Object.entries(CHAIN_SEARCH_TERMS).forEach(([term, chainIds]) => {
        if (term.includes(lowercaseQuery) || lowercaseQuery.includes(term)) {
            partialChainMatches = [...partialChainMatches, ...chainIds]
        }
    })

    // Remove duplicates from partial matches
    partialChainMatches = Array.from(new Set(partialChainMatches))

    return tokens.filter(token => {
        // Standard token name/symbol matching
        const matchesToken =
            token.name.toLowerCase().includes(lowercaseQuery) || token.symbol.toLowerCase().includes(lowercaseQuery)

        // Check if token's chain matches a partial blockchain name match
        const matchesChain = partialChainMatches.includes(token.chainId)

        if (!matchesToken && !matchesChain) return false

        // Additional filtering for selected token
        return filterBySelectedToken([token], selectedToken, side).length > 0
    })
}

// Helper function to filter out all tokens except SOL native token
export const filterSolGasAsset = (tokens: IToken[]): IToken[] => {
    // Only apply this filter if the ONLY_SOL_USDC feature flag is enabled
    if (!isFeatureEnabled('ONLY_SOL_USDC')) {
        return tokens // Return all tokens unchanged if feature is not enabled
    }

    return tokens.filter(token => {
        if (token.native && token.chainId == ChainId.Solana) return false

        return true
    })
}
